import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { RoomMatch } from "../../../api/room";
import { movieTitleHandler } from "../../../utils/helpers";
import styles from "./RoomMatchesSidebar.module.scss";
import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import LikeButton from "../../../components/shared/LikeButton";
import useLikeClick from "../../../hooks/useLikeClick";
import { setVoteCount } from "../../../store/slices/lists.slice";
import useInterfaceContext from "../../../components/providers/interface";
import { genresData } from "../../../constants/structure";
import SidebarHeadingIcon from "../../../components/icons/room/SidebarHeadingicon";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

interface RoomMatchesSidebarProps {
  matches: RoomMatch[];
  isOpen: boolean;
  isCreator: boolean;
  onClose: () => void;
  onSelectMatch: (matchId: number) => void;
}

const SKELETON_SLOTS = 3;

const RoomMatchesSidebar: React.FC<RoomMatchesSidebarProps> = ({
                                                                 matches,
                                                                 isOpen,
                                                                 isCreator,
                                                                 onSelectMatch,
                                                               }) => {
  const { t, i18n } = useTranslation();
  const { likesList, likeHandler } = useLikeClick();
  const { isMobile } = useInterfaceContext();
  const dispatch = useDispatch();
  const likedIds = Array.isArray(likesList?.data?.data) ? likesList.data.data : [];

  if (!isOpen) return null;

  const updateVoteCount = (id: any, key: any, type: any) => {
    dispatch(setVoteCount({ id, key, type }));
  };

  const formatLikesCount = (count: number) => {
    if (count < 1000) return `${count}`;
    const compactCount = Math.floor(count / 100) / 10;
    return `${Number.isInteger(compactCount) ? compactCount.toFixed(0) : compactCount.toFixed(1)}k`;
  };

  const renderMatchCard = (match: RoomMatch) => {
    const detail = match.movie_detail;
    const title = detail ? movieTitleHandler(i18n.language, detail) || detail.title : match.movie_title;
    const posterPath = detail?.poster_path || match.movie_poster_path;
    const year = detail?.release_date?.slice(0, 4);
    const imdbRating = detail ? (detail.imdb_rating?.toFixed(1) || detail.vote_average?.toFixed(1)) : null;
    const tomatoRating = detail ? detail.rotten_tomatoes_rating?.toFixed(0) : null;
    const likesCount = detail?.likes_count ?? 0;
    const liked = likedIds.includes(match.movie_id);

    const genreList = detail?.genres
      ? JSON.parse(detail.genres)
        .slice(0, 2)
        .map((id: number) => genresData.find((genre) => genre.id === id)?.name)
        .filter(Boolean)
        .map((name: string) => t(name))
      : [];

    return (
      <div
        key={match.id}
        className={`${styles.matchCard} ${styles.matchCardEnter} ${match.is_selected ? styles.matchCardSelected : ""} ${isCreator && !match.is_selected ? styles.matchCardClickable : ""}`}
        onClick={() => isCreator && !match.is_selected && onSelectMatch(match.id)}
      >
        <div className={styles.matchPoster}>
          {posterPath ? (
            <img src={`${IMG_BASE}${posterPath}`} alt={title} />
          ) : (
            <div className={styles.matchPosterPlaceholder}>{title?.charAt(0)}</div>
          )}
        </div>
        <div className={styles.matchInfo}>
          <div className={styles.matchTitle}>{title}</div>
          <div className={styles.matchMeta}>
            {year && <span className={styles.matchYear}>{year}</span>}
            {genreList.length > 0 && (
              <div className={styles.matchGenres}>{genreList.join(", ")}</div>
            )}
          </div>

          <div className={styles.matchActionsWrapper}>
            {imdbRating && (
              <div className={styles.ratingItem}>
                <OverlayRatingIcon width={10} height={10} />
                <p className={styles.rating}>{imdbRating}</p>
              </div>
            )}
            {tomatoRating && (
              <div className={styles.ratingItem}>
                <OverlayTomatoRatingIcon width={10} height={10} />
                <p className={styles.rating}>{tomatoRating}</p>
              </div>
            )}
            <LikeButton
              clickHandler={likeHandler}
              direction="row"
              isMobile={isMobile}
              count={formatLikesCount(likesCount)}
              updateFieldLocal={updateVoteCount}
              liked={liked}
              item={match}
              iconWidth={10}
              iconHeight={10}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSkeleton = (idx: number) => (
    <div key={`skeleton-${idx}`} className={`${styles.matchCard} ${styles.matchCardSkeleton}`}>
    </div>
  );

  const primarySlots = Array.from({ length: SKELETON_SLOTS }).map((_, idx) =>
    matches[idx] ? renderMatchCard(matches[idx]) : renderSkeleton(idx)
  );

  const extraMatches = matches.slice(SKELETON_SLOTS).map(renderMatchCard);

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <SidebarHeadingIcon/>

        {isMobile ? (
          <span className={styles.numberHolder}>
            Совпадения <span className={styles.number}>{matches.length > 0 && ` ${matches.length}`}</span>
          </span>
        ) : (
          <span>{t("room_common_choice", "Common Choice")}</span>
        )}

      </div>

      <div className={styles.matchList}>
        {primarySlots}
        {extraMatches}
      </div>
    </div>
  );
};

export default RoomMatchesSidebar;
